/*
 * Truwear — Horizon ↔ TruCustom variant-change bridge.
 *
 * INTERIM, remove at TruCustom app cutover (see theme-snippets/README.md).
 *
 * Why this file exists
 * -------------------
 * trucustom.js detects a variant change by listening for a `change` event on the
 * add-to-cart form and re-reading its [name="id"] input (see watchOneForm). That
 * works on Dawn-shaped themes, where the variant inputs sit inside the product
 * form. Neither half holds in Horizon:
 *
 *   1. `variant-picker` and `buy-buttons` are SIBLING blocks inside
 *      `_product-details`, so the picker's inputs are outside the <form> and its
 *      `change` events never reach a listener on that form.
 *   2. Horizon updates the variant input programmatically —
 *      `variantId.value = resource?.id ?? ''` in assets/product-form.js — which
 *      dispatches no `change` event.
 *
 * Left alone, trucustom.js never clears a finalized design when the customer
 * switches variants: they customize on Grey, switch to Navy, and the design rides
 * onto the wrong variant. That is the "navy order printed from a grey mockup"
 * failure trucustom.js's own comments call out.
 *
 * What this does
 * --------------
 * Listens for Horizon's variant-change event and re-dispatches it as the `change`
 * event trucustom.js is already waiting for, so THEIR logic decides what happens.
 * Nothing about the clear/re-gate/re-confirm behaviour is reimplemented here — a
 * local reimplementation would drift from the app-block delivery.
 *
 * This is a theme-side shim on purpose rather than a patch to assets/trucustom.js:
 * that file is generated from the TruCustom application repo and a local edit is
 * lost on its next build. The durable fix is upstream — have trucustom.js also
 * listen for `StandardEvents.productSelect` — at which point this file can go.
 */

import { StandardEvents } from '@shopify/events';

// Mirrors trucustom.js's own guard. buy-buttons.liquid renders once per product
// page today, but a merchant-added "Featured product" section would render it
// again, and this must not double-register.
if (!window.__TRUWEAR_TRUCUSTOM_BRIDGE__) {
  window.__TRUWEAR_TRUCUSTOM_BRIDGE__ = true;

  const PREFIX = '[TruCustom bridge]';
  const CART_FORM = 'form[action*="/cart/add"]';

  // Forms trucustom.js could have decorated, tracked so we can tell a value change
  // (fine) from the form node itself being replaced (not fine — see below).
  let knownForms = [];

  const cartForms = () =>
    Array.from(document.querySelectorAll(CART_FORM)).filter((form) => form.querySelector('[name="id"]'));

  function warn(message) {
    try {
      console.warn(`${PREFIX} ${message}`);
    } catch (error) {
      /* console unavailable — nothing useful to do */
    }
  }

  function notifyVariantChange() {
    const forms = cartForms();

    // A combined-listing option change morphs `main` wholesale
    // (assets/variant-picker.js), which can replace the form node outright.
    // trucustom.js fixes ctx.forms at boot, so its listeners — and the hidden
    // properties[...] inputs it injected — are attached to a node that is no
    // longer in the document. The design then cannot reach the cart line while
    // add-to-cart stays enabled, which is the one failure mode worth shouting
    // about. Nothing here can repair it (ctx is private to trucustom.js), so make
    // it loud instead of silent.
    const detached = knownForms.filter((form) => !document.contains(form));
    if (detached.length) {
      warn(
        'The add-to-cart form was replaced by a variant change (combined listing). ' +
          'trucustom.js still holds the old form, so a saved design may not reach the ' +
          'cart line. Reload the page before adding to cart, and do not mark combined-' +
          'listing products customizable until this is fixed in trucustom.js.'
      );
    }
    knownForms = forms;

    // bubbles: false — trucustom.js listens directly on the form, so the target
    // phase is enough. Horizon delegates its declarative `on:<event>` handlers
    // from document (assets/component.js), and a non-bubbling event cannot reach
    // that delegation, so this can't trip an unrelated theme handler.
    forms.forEach((form) => form.dispatchEvent(new Event('change', { bubbles: false })));
  }

  /*
   * Truwear — drop the visible `Design` line item property.
   *
   * Wanted on the order: `Design Preview` and `Design Artwork`. Not wanted: the
   * `Design` code, which showed to customers in the cart drawer and again in
   * checkout as "Design: INRnCZXs".
   *
   * Why the input is REMOVED rather than hidden: checkout is rendered by Shopify,
   * so no theme code runs there. Shopify only hides properties whose key starts
   * with `_`. The single way to keep `Design` out of both the cart and checkout
   * from the theme side is for it never to be submitted.
   *
   * Why a MutationObserver rather than a submit handler: Horizon's "Buy it now"
   * and the express checkout buttons go straight to checkout from the form's
   * contents without firing a `submit` event, so a submit-time strip would miss
   * them and the property would still reach checkout on those paths. Removing the
   * input as soon as trucustom.js injects it covers every route to checkout.
   *
   * Safe against trucustom.js's own bookkeeping: it resolves a design by
   * `_tc_design_token` (still submitted), and pollCartThenLock()/cartContainsToken()
   * match the session token against ANY property value, which `_tc_design_token`
   * still satisfies.
   *
   * The tidier fix is upstream — stop returning `Design` from
   * /api/v1/widget/designs/<token>/line_item_properties, or rename the key with a
   * leading underscore — which would also cover the app-block delivery. This lives
   * here so it survives the next `bin/build-theme-snippets`.
   */
  const DROPPED_PROPERTY = 'properties[Design]';
  let reportedDrop = false;

  function dropUnwantedProperties(scope) {
    if (!scope || typeof scope.querySelectorAll !== 'function') return;

    const inputs =
      scope.matches?.(`input[name="${DROPPED_PROPERTY}"]`) === true
        ? [scope]
        : Array.from(scope.querySelectorAll(`input[name="${DROPPED_PROPERTY}"]`));

    inputs.forEach((input) => {
      input.remove();

      // Once per page: enough to confirm in QA that this ran, without narrating
      // every re-finalize.
      if (!reportedDrop) {
        reportedDrop = true;
        warn(`Removed the "${DROPPED_PROPERTY}" line item property before submission (intentional).`);
      }
    });
  }

  // trucustom.js appends the property inputs straight onto the form, and re-appends
  // them on every finalize and on the reload restore path, so this has to keep
  // watching rather than sweep once.
  new MutationObserver((records) => {
    records.forEach((record) => {
      record.addedNodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE) dropUnwantedProperties(node);
      });
    });
  }).observe(document.documentElement, { childList: true, subtree: true });

  document.addEventListener(StandardEvents.productSelect, (event) => {
    // Horizon writes the new variant into the form's [name="id"] only after
    // `await event.promise` resolves (assets/product-form.js #onProductSelect), so
    // reading it any earlier gets the previous variant. Its listener is on an
    // ancestor of ours, so it attaches to this promise first and its continuation
    // runs before ours.
    //
    // trucustom.js re-reads the input itself and no-ops when the value is
    // unchanged, so a redundant notification here is harmless — a missed one is
    // not. Notify on both settle paths.
    const promise = event.promise;

    if (promise && typeof promise.then === 'function') {
      promise.then(notifyVariantChange, notifyVariantChange);
    } else {
      notifyVariantChange();
    }
  });

  // Seed the form list so the first variant change can detect a replaced node, and
  // sweep once in case an injection landed before the observer was watching.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      knownForms = cartForms();
      dropUnwantedProperties(document);
    });
  } else {
    knownForms = cartForms();
    dropUnwantedProperties(document);
  }
}
